import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import CountUp from 'react-countup';
import {
  Users, FileText, CheckCircle, Clock, TrendingUp,
  MessageSquare, PlayCircle, HelpCircle, ArrowRight, LayoutDashboard,
  GripVertical, Save, RotateCcw, ShieldCheck, Settings2, X, Eye, EyeOff,
  BarChart3, Package, Activity, Newspaper, Megaphone, Hash, Crown, Building2,
  UserPlus
} from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import usePageTitle from '../hooks/usePageTitle';

// Shared dashboard widgets
import OpportunityTicker from '../components/dashboard/OpportunityTicker';
import NewsCarousel from '../components/dashboard/NewsCarousel';
import AnnouncementsPanel from '../components/dashboard/AnnouncementsPanel';
import ActivityFeed from '../components/dashboard/ActivityFeed';
import OpportunitiesList from '../components/dashboard/OpportunitiesList';
import CommodityTracker from '../components/dashboard/CommodityTracker';
import TasksWidget from '../components/dashboard/TasksWidget';

// Module metadata for the settings panel (same as user dashboard)
const MODULE_META = {
  animated_counters: { label: 'Animated Counters', icon: BarChart3, description: 'Stats with count-up animation' },
  latest_opportunities: { label: 'Opportunities', icon: Package, description: 'Deal listings' },
  activity_feed: { label: 'Activity Feed', icon: Activity, description: 'Live platform activity' },
  news_articles: { label: 'News Articles', icon: Newspaper, description: 'Latest news carousel' },
  announcements: { label: 'Announcements', icon: Megaphone, description: 'Platform announcements' },
  commodity_tracker: { label: 'Commodity Tracker', icon: Hash, description: 'Commodity price grid' },
  opportunity_ticker: { label: 'Opportunity Ticker', icon: TrendingUp, description: 'Scrolling deal ticker' },
};

const DEFAULT_MODULE_ORDER = [
  'animated_counters', 'latest_opportunities', 'activity_feed',
  'news_articles', 'announcements', 'commodity_tracker', 'opportunity_ticker'
];

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loiStats, setLoiStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const { adminTheme } = useTheme();
  const { user } = useAuth();

  const isMainAdmin = user?.role && ['admin', 'main_admin'].includes(user.role);

  usePageTitle('Admin Dashboard');

  const defaultStatsOrder = isMainAdmin
    ? ['total_users', 'pending_users', 'total_deals', 'total_companies', 'total_tickets', 'total_requests']
    : ['total_users', 'pending_users', 'total_deals', 'total_tickets', 'total_requests'];

  const [statsOrder, setStatsOrder] = useState(defaultStatsOrder);
  const [cardsOrder, setCardsOrder] = useState([
    'users', 'approvals', 'opportunities', 'requests', 'announcements', 'support', 'videos', 'invite'
  ]);

  // User dashboard module data
  const [dashboardConfig, setDashboardConfig] = useState(null);
  const [tickerDeals, setTickerDeals] = useState([]);
  const [activities, setActivities] = useState([]);
  const [articles, setArticles] = useState([]);
  const [announcementsList, setAnnouncementsList] = useState([]);
  const [latestDeals, setLatestDeals] = useState([]);

  // User preferences state (module show/hide/reorder)
  const [showSettings, setShowSettings] = useState(false);
  const [moduleOrder, setModuleOrder] = useState(DEFAULT_MODULE_ORDER);
  const [hiddenModules, setHiddenModules] = useState([]);
  const [savingPrefs, setSavingPrefs] = useState(false);

  const pageStyles = adminTheme?.page || {};
  const cardStyles = adminTheme?.cards || {};
  const buttonStyles = adminTheme?.buttons || {};
  const primaryColor = buttonStyles.primaryColor || '#2B4B6A';
  const secondaryColor = buttonStyles.secondaryColor || '#F58A5A';

  useEffect(() => {
    fetchAllData();
    fetchDashboardLayout();
    fetchPreferences();
  }, []);

  const fetchAllData = async () => {
    try {
      const [
        statsRes, loiRes, configRes, tickerRes, activityRes, newsRes, announcementsRes, dealsRes
      ] = await Promise.all([
        api.get('/admin/stats').catch(() => ({ data: {} })),
        api.get('/admin/loi-verification/stats').catch(() => ({ data: null })),
        api.get('/dashboard/config').catch(() => ({ data: {} })),
        api.get('/dashboard/ticker').catch(() => ({ data: [] })),
        api.get('/dashboard/activity').catch(() => ({ data: [] })),
        api.get('/news/latest?limit=5').catch(() => ({ data: { articles: [] } })),
        api.get('/announcements').catch(() => ({ data: [] })),
        api.get('/deals').catch(() => ({ data: [] })),
      ]);
      setStats(statsRes.data);
      setLoiStats(loiRes.data);
      setDashboardConfig(configRes.data);
      setActivities(activityRes.data);
      setArticles(newsRes.data?.articles || []);
      setAnnouncementsList(Array.isArray(announcementsRes.data) ? announcementsRes.data.slice(0, 5) : []);
      const allDeals = Array.isArray(dealsRes.data) ? dealsRes.data : [];
      setLatestDeals(allDeals.slice(0, 10));

      // Use ticker endpoint data, fall back to deals list for the scrolling ticker
      const tickerData = Array.isArray(tickerRes.data) ? tickerRes.data : [];
      if (tickerData.length > 0) {
        setTickerDeals(tickerData);
      } else if (allDeals.length > 0) {
        // Build ticker-compatible objects from deals
        setTickerDeals(allDeals.slice(0, 20).map(d => ({
          id: d._id || d.id,
          title: d.title || '',
          commodity: d.commodity || d.category_name || '',
          quantity: d.quantity || '',
          price: d.price || '',
          country: d.country || '',
          created_at: d.created_at || '',
        })));
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardLayout = async () => {
    try {
      const response = await api.get('/admin/settings/dashboard-layout');
      if (response.data.stats_order?.length > 0) {
        setStatsOrder(response.data.stats_order);
      }
      if (response.data.cards_order?.length > 0) {
        setCardsOrder(response.data.cards_order);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard layout:', error);
    }
  };

  const fetchPreferences = async () => {
    try {
      const res = await api.get('/dashboard/preferences');
      if (res.data.module_order?.length > 0) {
        setModuleOrder(res.data.module_order);
      }
      setHiddenModules(res.data.hidden_modules || []);
    } catch (error) {
      // Use defaults silently
    }
  };

  const savePreferences = async () => {
    setSavingPrefs(true);
    try {
      await api.put('/dashboard/preferences', {
        module_order: moduleOrder,
        hidden_modules: hiddenModules,
      });
      toast.success('Dashboard layout saved');
      setShowSettings(false);
    } catch (error) {
      toast.error('Failed to save preferences');
    } finally {
      setSavingPrefs(false);
    }
  };

  const saveDashboardLayout = async () => {
    try {
      await api.put('/admin/settings/dashboard-layout', {
        stats_order: statsOrder,
        cards_order: cardsOrder
      });
      toast.success('Admin layout saved!');
      setHasChanges(false);
      setEditMode(false);
    } catch (error) {
      toast.error('Failed to save layout');
    }
  };

  const resetLayout = () => {
    const defaultStats = isMainAdmin
      ? ['total_users', 'pending_users', 'total_deals', 'total_companies', 'total_tickets', 'total_requests']
      : ['total_users', 'pending_users', 'total_deals', 'total_tickets', 'total_requests'];
    setStatsOrder(defaultStats);
    setCardsOrder(['users', 'approvals', 'opportunities', 'requests', 'announcements', 'support', 'videos', 'invite']);
    setHasChanges(true);
  };

  const onDragEnd = useCallback((result) => {
    if (!result.destination) return;
    const { source, destination, type } = result;
    if (type === 'stats') {
      const newOrder = Array.from(statsOrder);
      const [removed] = newOrder.splice(source.index, 1);
      newOrder.splice(destination.index, 0, removed);
      setStatsOrder(newOrder);
      setHasChanges(true);
    } else if (type === 'cards') {
      const newOrder = Array.from(cardsOrder);
      const [removed] = newOrder.splice(source.index, 1);
      newOrder.splice(destination.index, 0, removed);
      setCardsOrder(newOrder);
      setHasChanges(true);
    }
  }, [statsOrder, cardsOrder]);

  // Module preferences helpers
  const isAdminEnabled = (key) => dashboardConfig?.[key]?.enabled !== false;
  const isModuleVisible = (key) => isAdminEnabled(key) && !hiddenModules.includes(key);

  const toggleModuleVisibility = (key) => {
    setHiddenModules(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  const onModuleDragEnd = (result) => {
    if (!result.destination) return;
    const newOrder = Array.from(moduleOrder);
    const [removed] = newOrder.splice(result.source.index, 1);
    newOrder.splice(result.destination.index, 0, removed);
    setModuleOrder(newOrder);
  };

  // Stat configurations
  const statConfigs = {
    total_users: {
      icon: Users, label: 'Total Users', getValue: () => stats?.total_users || 0,
      color: { bg: '#EFF6FF', border: '#BFDBFE', icon: primaryColor, text: '#1E40AF' }
    },
    pending_users: {
      icon: Clock, label: 'Pending Approvals', getValue: () => stats?.pending_users || 0,
      color: { bg: '#FFF7ED', border: '#FED7AA', icon: secondaryColor, text: '#9A3412' }
    },
    total_deals: {
      icon: TrendingUp, label: 'Total Opportunities', getValue: () => stats?.total_deals || 0,
      color: { bg: '#F0FDF4', border: '#BBF7D0', icon: '#059669', text: '#166534' }
    },
    total_companies: {
      icon: Users, label: 'Companies', getValue: () => stats?.total_companies || 0,
      color: { bg: '#FAF5FF', border: '#E9D5FF', icon: '#7C3AED', text: '#6B21A8' }
    },
    total_tickets: {
      icon: MessageSquare, label: 'Support Tickets', getValue: () => stats?.total_tickets || 0,
      color: { bg: '#EDE9FE', border: '#DDD6FE', icon: '#7C3AED', text: '#6B21A8' }
    },
    total_requests: {
      icon: FileText, label: 'Quote Requests', getValue: () => stats?.total_requests || 0,
      color: { bg: '#F0FDFA', border: '#99F6E4', icon: '#0D9488', text: '#115E59' }
    }
  };

  const cardConfigs = {
    users: { to: '/admin/users', icon: Users, title: 'Manage Users', description: 'View and manage all registered users', iconBg: `${primaryColor}15`, iconColor: primaryColor },
    approvals: { to: '/admin/tier-approvals', icon: CheckCircle, title: 'Approve Users', description: 'Review pending tier verifications', iconBg: `${secondaryColor}15`, iconColor: secondaryColor, getBadge: () => stats?.pending_users > 0 ? `${stats.pending_users} pending` : null },
    opportunities: { to: '/admin/deals', icon: TrendingUp, title: 'Manage Opportunities', description: 'Create and manage trading opportunities', iconBg: '#D1FAE5', iconColor: '#059669' },
    requests: { to: '/admin/quote-requests', icon: FileText, title: 'Manage Requests', description: 'View and process quote requests', iconBg: '#DBEAFE', iconColor: '#2563EB', getBadge: () => stats?.pending_requests > 0 ? `${stats.pending_requests} new` : null },
    announcements: { to: '/admin/announcements', icon: MessageSquare, title: 'Announcements', description: 'Post news and updates', iconBg: `${primaryColor}15`, iconColor: primaryColor },
    support: { to: '/admin/support', icon: HelpCircle, title: 'Support Tickets', description: 'Handle customer support requests', iconBg: '#EDE9FE', iconColor: '#7C3AED' },
    videos: { to: '/admin/videos', icon: PlayCircle, title: 'Video Content', description: 'Manage educational videos', iconBg: '#FEE2E2', iconColor: '#DC2626' },
    invite: { to: '/admin/invite', icon: UserPlus, title: 'Invite & Grow', description: 'Invite people to the platform', iconBg: '#DBEAFE', iconColor: '#2563EB' }
  };

  // StatCard
  const StatCard = ({ config, isDragging }) => {
    const Icon = config.icon;
    const c = config.color;
    return (
      <div
        className={`rounded-xl p-3 sm:p-4 transition-all duration-200 flex flex-col justify-between min-h-[90px] sm:min-h-[100px] min-w-0 overflow-hidden ${isDragging ? 'shadow-lg ring-2 ring-blue-400' : ''} ${editMode ? 'cursor-grab' : ''}`}
        style={{ backgroundColor: c.bg, border: `1px solid ${c.border}` }}
      >
        <div className="flex items-start justify-between gap-1 sm:gap-2">
          {editMode && <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />}
          <p className="text-xs font-medium text-gray-500 leading-tight flex-1 min-w-0 truncate">{config.label}</p>
          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${c.icon}15` }}>
            <Icon className="w-4 h-4 sm:w-5 sm:h-5" style={{ color: c.icon }} />
          </div>
        </div>
        <div className="mt-2">
          <CountUp end={config.getValue()} duration={2} separator="," className="text-2xl sm:text-3xl font-bold tabular-nums leading-none" style={{ color: c.text }} />
        </div>
      </div>
    );
  };

  // ActionCard
  const ActionCard = ({ config, isDragging }) => {
    const Icon = config.icon;
    const badge = config.getBadge ? config.getBadge() : null;
    const content = (
      <div className={`group rounded-xl p-4 sm:p-5 shadow-sm transition-all relative overflow-hidden min-w-0 ${isDragging ? 'shadow-lg ring-2 ring-blue-400' : 'hover:shadow-md'} ${editMode ? 'cursor-grab' : ''}`} style={{ backgroundColor: cardStyles.backgroundColor || '#FFFFFF', border: `1px solid ${cardStyles.borderColor || '#E5E7EB'}` }}>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            {editMode && <GripVertical className="w-5 h-5 text-gray-400 flex-shrink-0" />}
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center transition flex-shrink-0" style={{ backgroundColor: config.iconBg }}>
              <Icon className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: config.iconColor }} />
            </div>
          </div>
          {!editMode && <ArrowRight className="w-5 h-5 text-gray-300 group-hover:text-gray-500 group-hover:translate-x-1 transition-all flex-shrink-0" />}
        </div>
        <h3 className="text-sm sm:text-base font-medium mt-3 truncate" style={{ color: cardStyles.headerColor || '#111827' }}>{config.title}</h3>
        <p className="text-xs sm:text-sm mt-1 truncate" style={{ color: cardStyles.textColor || '#6B7280' }}>{config.description}</p>
        {badge && <span className="absolute top-3 right-3 sm:top-4 sm:right-4 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full text-xs font-medium text-white" style={{ backgroundColor: secondaryColor }}>{badge}</span>}
      </div>
    );
    if (editMode) return content;
    return <Link to={config.to}>{content}</Link>;
  };

  // Render a module by key (for dynamic ordering)
  const renderModule = (key) => {
    switch (key) {
      case 'animated_counters':
        return null; // Part of admin stat cards, not rendered here
      case 'latest_opportunities':
        if (!isModuleVisible('latest_opportunities')) return null;
        return (
          <OpportunitiesList
            key={key}
            deals={latestDeals}
            title="Latest Opportunities"
            showMyDeals={false}
            maxItems={dashboardConfig?.latest_opportunities?.count || 5}
          />
        );
      case 'activity_feed':
        if (!isModuleVisible('activity_feed')) return null;
        return <ActivityFeed key={key} activities={activities} />;
      case 'news_articles':
        if (!isModuleVisible('news_articles')) return null;
        return <NewsCarousel key={key} articles={articles} />;
      case 'announcements':
        if (!isModuleVisible('announcements')) return null;
        return (
          <AnnouncementsPanel
            key={key}
            announcements={announcementsList}
            maxItems={dashboardConfig?.announcements?.count || 5}
          />
        );
      case 'commodity_tracker':
        if (!isModuleVisible('commodity_tracker')) return null;
        return <CommodityTracker key={key} count={dashboardConfig?.commodity_tracker?.count || 6} />;
      case 'opportunity_ticker':
        return null; // Rendered separately at the bottom (full width)
      default:
        return null;
    }
  };

  // Build ordered list of visible modules (excluding ticker)
  const orderedModules = moduleOrder
    .filter(key => key !== 'animated_counters' && key !== 'opportunity_ticker')
    .map(key => renderModule(key))
    .filter(Boolean);

  // Settings panel data (computed inline, not in sub-component)
  const settingsVisibleModules = moduleOrder.filter(k => !hiddenModules.includes(k) && isAdminEnabled(k));
  const settingsHiddenByUser = moduleOrder.filter(k => hiddenModules.includes(k) && isAdminEnabled(k));
  const settingsDisabledByAdmin = DEFAULT_MODULE_ORDER.filter(k => !isAdminEnabled(k));

  if (loading) {
    return (
      <Sidebar>
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: pageStyles.backgroundColor }}>
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-300" style={{ borderTopColor: primaryColor }} />
            <p className="text-sm text-gray-500">Loading dashboard...</p>
          </div>
        </div>
      </Sidebar>
    );
  }

  return (
    <Sidebar>
      <div className="p-4 sm:p-6 lg:p-8 min-h-screen relative overflow-hidden" style={{ backgroundColor: pageStyles.backgroundColor || '#F9FAFB' }}>
        {/* Settings button */}
        <button
          onClick={() => setShowSettings(true)}
          className="absolute top-4 right-4 sm:top-6 sm:right-6 lg:top-8 lg:right-8 p-2 rounded-lg bg-white border border-gray-200 shadow-sm hover:shadow-md hover:bg-gray-50 transition z-10"
          title="Customize Dashboard"
        >
          <Settings2 className="w-5 h-5 text-gray-500" />
        </button>

        {/* Dashboard Type Indicator */}
        <div className="flex items-center gap-2 mb-4">
          <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${
            isMainAdmin ? 'bg-amber-100 text-amber-700' : 'bg-indigo-100 text-indigo-700'
          }`}>
            {isMainAdmin ? <Crown className="w-3.5 h-3.5" /> : <Building2 className="w-3.5 h-3.5" />}
            {isMainAdmin ? 'Super Admin Dashboard' : 'Company Admin Dashboard'}
          </span>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
          <div className="flex items-center gap-3">
            <div className="p-2.5 sm:p-3 rounded-xl" style={{ backgroundColor: primaryColor }}>
              <LayoutDashboard className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
            </div>
            <div>
              <h1 className="font-semibold" style={{ color: pageStyles.titleColor || '#111827', fontSize: pageStyles.titleSize || '24px' }}>
                Dashboard
              </h1>
              <p style={{ color: pageStyles.textColor || '#6B7280' }} className="text-sm mt-0.5">
                Overview of your platform activity
              </p>
            </div>
          </div>

          {/* Edit Mode Controls (admin stat cards / action cards) */}
          <div className="flex items-center gap-2 flex-wrap">
            {editMode ? (
              <>
                <button onClick={resetLayout} className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition">
                  <RotateCcw className="w-4 h-4" /><span className="hidden sm:inline">Reset</span>
                </button>
                <button onClick={() => { setEditMode(false); setHasChanges(false); fetchDashboardLayout(); }} className="px-3 sm:px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition">Cancel</button>
                <button onClick={saveDashboardLayout} disabled={!hasChanges} className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm text-white rounded-lg transition disabled:opacity-50" style={{ backgroundColor: primaryColor }}>
                  <Save className="w-4 h-4" /><span className="hidden sm:inline">Save Layout</span><span className="sm:hidden">Save</span>
                </button>
              </>
            ) : (
              <button onClick={() => setEditMode(true)} className="flex items-center gap-2 px-3 sm:px-4 py-2 text-sm text-white rounded-lg transition" style={{ backgroundColor: primaryColor }}>
                <GripVertical className="w-4 h-4" /><span className="hidden sm:inline">Customize Admin Cards</span><span className="sm:hidden">Customize</span>
              </button>
            )}
          </div>
        </div>

        {editMode && (
          <div className="mb-6 p-3 sm:p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <p className="text-blue-800 text-xs sm:text-sm">
              <strong>Edit Mode:</strong> Drag and drop the stat cards and action cards to rearrange. Click "Save Layout" when done. Use the gear icon (top-right) to show/hide dashboard modules.
            </p>
          </div>
        )}

        <DragDropContext onDragEnd={onDragEnd}>
          {/* Key Metrics - animated counters */}
          <Droppable droppableId="stats" direction="horizontal" type="stats">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 sm:gap-4 mb-6 sm:mb-8">
                {statsOrder.map((key, index) => {
                  const config = statConfigs[key];
                  if (!config) return null;
                  return (
                    <Draggable key={key} draggableId={key} index={index} isDragDisabled={!editMode}>
                      {(provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className="min-w-0">
                          <StatCard config={config} isDragging={snapshot.isDragging} />
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>

          {/* Quick Actions */}
          <Droppable droppableId="cards" direction="horizontal" type="cards">
            {(provided) => (
              <div ref={provided.innerRef} {...provided.droppableProps} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {cardsOrder.map((key, index) => {
                  const config = cardConfigs[key];
                  if (!config) return null;
                  return (
                    <Draggable key={key} draggableId={key} index={index} isDragDisabled={!editMode}>
                      {(provided, snapshot) => (
                        <div ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps} className="min-w-0">
                          <ActionCard config={config} isDragging={snapshot.isDragging} />
                        </div>
                      )}
                    </Draggable>
                  );
                })}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>

        {/* LOI Verification Widget */}
        {isMainAdmin && loiStats && (
          <div className="mt-6 sm:mt-8">
            <Link
              to="/admin/external-services?tab=verifications"
              className="block rounded-xl p-4 sm:p-5 shadow-sm hover:shadow-md transition-shadow"
              style={{ backgroundColor: cardStyles.backgroundColor || '#FFFFFF', border: `1px solid ${cardStyles.borderColor || '#E5E7EB'}` }}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: `${primaryColor}15` }}>
                    <ShieldCheck className="w-5 h-5" style={{ color: primaryColor }} />
                  </div>
                  <h3 className="font-semibold text-gray-900">LOI Verifications</h3>
                </div>
                <ArrowRight className="w-4 h-4 text-gray-400" />
              </div>
              <div className="flex items-center gap-3 sm:gap-6 flex-wrap">
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-lg sm:text-2xl font-bold text-green-600">{loiStats.passed || 0}</span>
                  <span className="text-gray-400">/</span>
                  <span className="text-lg sm:text-2xl font-bold text-red-500">{loiStats.failed || 0}</span>
                  <span className="text-xs text-gray-400 ml-1">Good / Bad</span>
                </div>
                {loiStats.total > 0 && (
                  <div className="flex-1">
                    <div className="flex h-3 rounded-full overflow-hidden bg-gray-100">
                      <div className="bg-green-500 transition-all" style={{ width: `${(loiStats.passed / loiStats.total) * 100}%` }} />
                      <div className="bg-red-400 transition-all" style={{ width: `${(loiStats.failed / loiStats.total) * 100}%` }} />
                    </div>
                  </div>
                )}
              </div>
              {loiStats.today_total > 0 && (
                <p className="text-xs text-gray-400 mt-2">Today: {loiStats.today_passed} good / {loiStats.today_failed} bad ({loiStats.today_total} total)</p>
              )}
            </Link>
          </div>
        )}

        {/* My Tasks Widget */}
        <div className="mt-6 sm:mt-8">
          <TasksWidget maxItems={5} />
        </div>

        {/* ========== User Dashboard Modules (dynamic order) ========== */}
        {orderedModules.length > 0 && (
          <div className="mt-6 sm:mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
              {orderedModules}
            </div>
          </div>
        )}

        {/* Ticker (always at bottom, full width) */}
        {tickerDeals.length > 0 && !hiddenModules.includes('opportunity_ticker') && (
          <div className="mt-4 sm:mt-6">
            <OpportunityTicker
              deals={tickerDeals}
              speed={dashboardConfig?.opportunity_ticker?.speed || 'normal'}
            />
          </div>
        )}

        {/* Settings Panel (inline JSX, not a sub-component) */}
        {showSettings && (
          <>
            <div className="fixed inset-0 bg-black/30 z-40 transition-opacity" onClick={() => setShowSettings(false)} />
            <div className="fixed top-0 right-0 h-full w-full sm:w-96 bg-white shadow-2xl z-50 flex flex-col overflow-hidden">
              <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Customize Dashboard</h2>
                  <p className="text-xs text-gray-500 mt-0.5">Drag to reorder, toggle to show/hide</p>
                </div>
                <button onClick={() => setShowSettings(false)} className="p-2 rounded-lg hover:bg-gray-100 transition">
                  <X className="w-5 h-5 text-gray-500" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Visible Modules</h3>
                  <DragDropContext onDragEnd={onModuleDragEnd}>
                    <Droppable droppableId="admin-modules">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps} className="space-y-2">
                          {settingsVisibleModules.map((key, index) => {
                            const meta = MODULE_META[key];
                            if (!meta) return null;
                            const IconComp = meta.icon;
                            return (
                              <Draggable key={key} draggableId={key} index={index}>
                                {(provided, snapshot) => (
                                  <div ref={provided.innerRef} {...provided.draggableProps}
                                    className={`flex items-center gap-3 p-3 rounded-lg border transition ${snapshot.isDragging ? 'shadow-lg ring-2 ring-blue-400 bg-white' : 'bg-gray-50 border-gray-200'}`}>
                                    <div {...provided.dragHandleProps} className="cursor-grab"><GripVertical className="w-4 h-4 text-gray-400" /></div>
                                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#EFF6FF' }}>
                                      <IconComp className="w-4 h-4 text-blue-600" />
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <p className="text-sm font-medium text-gray-800">{meta.label}</p>
                                      <p className="text-xs text-gray-500 truncate">{meta.description}</p>
                                    </div>
                                    <button onClick={() => toggleModuleVisibility(key)} className="p-1.5 rounded-lg hover:bg-gray-200 transition flex-shrink-0" title="Hide module">
                                      <Eye className="w-4 h-4 text-gray-500" />
                                    </button>
                                  </div>
                                )}
                              </Draggable>
                            );
                          })}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                  {settingsVisibleModules.length === 0 && <p className="text-sm text-gray-400 text-center py-4">All modules are hidden</p>}
                </div>

                {settingsHiddenByUser.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Hidden Modules</h3>
                    <div className="space-y-2">
                      {settingsHiddenByUser.map(key => {
                        const meta = MODULE_META[key];
                        if (!meta) return null;
                        const IconComp = meta.icon;
                        return (
                          <div key={key} className="flex items-center gap-3 p-3 rounded-lg border border-dashed border-gray-200 bg-white opacity-70">
                            <div className="w-4" />
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F3F4F6' }}>
                              <IconComp className="w-4 h-4 text-gray-400" />
                            </div>
                            <div className="flex-1 min-w-0"><p className="text-sm font-medium text-gray-500">{meta.label}</p></div>
                            <button onClick={() => toggleModuleVisibility(key)} className="p-1.5 rounded-lg hover:bg-gray-100 transition flex-shrink-0" title="Show module">
                              <EyeOff className="w-4 h-4 text-gray-400" />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {settingsDisabledByAdmin.length > 0 && (
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Disabled by Admin</h3>
                    <div className="space-y-2">
                      {settingsDisabledByAdmin.map(key => {
                        const meta = MODULE_META[key];
                        if (!meta) return null;
                        const IconComp = meta.icon;
                        return (
                          <div key={key} className="flex items-center gap-3 p-3 rounded-lg border border-gray-100 bg-gray-50 opacity-50">
                            <div className="w-4" />
                            <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 bg-gray-100"><IconComp className="w-4 h-4 text-gray-300" /></div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-gray-400">{meta.label}</p>
                              <p className="text-xs text-gray-300">Disabled by administrator</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>

              <div className="px-5 py-4 border-t border-gray-200 flex gap-3">
                <button onClick={() => { setModuleOrder(DEFAULT_MODULE_ORDER); setHiddenModules([]); }} className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition">
                  Reset to Default
                </button>
                <button onClick={savePreferences} disabled={savingPrefs} className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition disabled:opacity-50">
                  <Save className="w-4 h-4" />
                  {savingPrefs ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </Sidebar>
  );
};

export default AdminDashboard;
